import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { NzModalRef } from 'ng-zorro-antd/modal';
import Chart, { ChartData } from 'chart.js/auto';
import { NzButtonModule } from 'ng-zorro-antd/button';

import { IParkingDetail } from '../models/parking-map';

@Component({
  selector: 'aggregator-parking-chart',
  standalone: true,
  imports: [NzButtonModule],
  templateUrl: './parking-chart.component.html',
  styleUrls: ['./parking-chart.component.scss'],
})
export class ParkingChartComponent implements OnInit, AfterViewInit {
  @Input() nzData!: { parkingInfo: IParkingDetail };
  chartData!: ChartData<'line'>;
  chart!: Chart<'line'>;
  activePeriod = 'hours';

  // Моковые данные для разных периодов
  private mockData: { [key: string]: number[] } = {
    hours: [
      5, 4, 3, 2, 1, 0, 2, 5, 10, 38, 30, 25, 25, 30, 34, 32, 28, 25, 20, 18, 15, 10, 8, 6, 4,
    ],
    days: [10, 15, 20, 25, 30, 34, 32, 28, 25, 20, 18, 15, 10, 8, 6, 4],
    weeks: [20, 25, 30, 34, 32, 28, 25],
    months: [
      10, 12, 15, 18, 20, 22, 25, 28, 30, 32, 34, 32, 30, 28, 26, 24, 22, 20, 18, 16, 14, 12, 10, 8,
      6, 4, 2, 1, 0, 2,
    ],
  };

  // Метки для разных периодов
  private labels: { [key: string]: string[] } = {
    hours: [
      '00:00',
      '01:00',
      '02:00',
      '03:00',
      '04:00',
      '05:00',
      '06:00',
      '07:00',
      '08:00',
      '09:00',
      '10:00',
      '11:00',
      '12:00',
      '13:00',
      '14:00',
      '15:00',
      '16:00',
      '17:00',
      '18:00',
      '19:00',
      '20:00',
      '21:00',
      '22:00',
      '23:00',
    ],
    days: Array.from({ length: 16 }, (_, i) => `День ${i + 1}`),
    weeks: Array.from({ length: 7 }, (_, i) => `Нед ${i + 1}`),
    months: Array.from({ length: 30 }, (_, i) => `${i + 1} мая`),
  };

  constructor(private modalRef: NzModalRef) {}

  ngOnInit(): void {
    this.updateChartData();
  }

  ngAfterViewInit(): void {
    this.createChart();
  }

  // Обновление данных графика в зависимости от выбранного периода
  updateChartData(): void {
    const data = this.mockData[this.activePeriod];
    const labels = this.labels[this.activePeriod];
    const maxY = Math.max(...data) + 5; // Устанавливаем максимум с запасом

    this.chartData = {
      labels,
      datasets: [
        {
          data,
          fill: true,
        },
      ],
    };

    if (this.chart) {
      if (this.chart.options.scales?.['y']) {
        this.chart.options.scales['y'].max = maxY;
      }
      this.chart.data = this.chartData;
      this.chart.update();
      this.applyGradients();
    }
  }

  createChart(): void {
    const ctx = document.getElementById('parkingChart') as HTMLCanvasElement;
    this.chart = new Chart<'line'>(ctx, {
      type: 'line',
      data: this.chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            position: 'right',
            max: 40,
            grid: {
              color: 'rgba(0, 0, 0, 0.1)',
            },
          },
          x: {
            grid: {
              display: false,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: context => {
                const label = context.dataset.label || '';
                const value = context.parsed.y || 0;
                return `${label}: ${value} мест`;
              },
            },
          },
        },
        animation: {
          duration: 1000,
          easing: 'easeInOutQuad',
        },
      },
    });

    this.applyGradients();
  }

  // Применение градиентов
  private applyGradients(): void {
    const chartCtx = this.chart.ctx;
    const gradientFill = chartCtx.createLinearGradient(0, 0, 0, 250);
    gradientFill.addColorStop(1, 'rgba(76, 175, 80, 0.9)');
    gradientFill.addColorStop(0.5, 'rgba(255, 193, 7, 0.9)');
    gradientFill.addColorStop(0, 'rgba(244, 67, 54, 0.8)');

    const gradientStroke = chartCtx.createLinearGradient(0, 0, 0, 250);
    gradientStroke.addColorStop(1, '#4CAF50E5');
    gradientStroke.addColorStop(0.5, '#FFC107E5');
    gradientStroke.addColorStop(0, '#F44336');

    this.chartData.datasets[0].backgroundColor = gradientFill;
    this.chartData.datasets[0].borderColor = gradientStroke;
    this.chart.update();
  }

  setActivePeriod(period: string): void {
    this.activePeriod = period;
    this.updateChartData();
  }
}
